import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    Plus, Search, FileText, Calendar, Users, BarChart3, Clock,
    BookOpen, Layers, CalendarDays, CalendarRange, Target,
    Eye, Edit, Trash2, Play
} from 'lucide-react'
import { mockTests, mockTestResults, getBatches, Test, TestCategory, TestManagementStatus } from '@/data/mentor-mock-data'
import { CreateTestView } from './create-test-view'
import { toast } from 'sonner'

const categoryConfig: Record<TestCategory, { label: string; icon: React.ElementType; color: string }> = {
    chapter: { label: 'Chapter-wise', icon: BookOpen, color: 'blue' },
    daily: { label: 'Daily', icon: CalendarDays, color: 'green' },
    weekly: { label: 'Weekly', icon: CalendarRange, color: 'purple' },
    monthly: { label: 'Monthly', icon: Calendar, color: 'orange' },
    full: { label: 'Full Series', icon: Target, color: 'red' }
}

const statusConfig: Record<TestManagementStatus, { label: string; bg: string; text: string }> = {
    draft: { label: 'Draft', bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400' },
    published: { label: 'Published', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
    upcoming: { label: 'Upcoming', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
    active: { label: 'Active', bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
    completed: { label: 'Completed', bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' }
}

export function TestManagementView() {
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedCategory, setSelectedCategory] = useState<TestCategory | 'all'>('all')
    const [selectedStatus, setSelectedStatus] = useState<TestManagementStatus | 'all'>('all')
    const [selectedBatch, setSelectedBatch] = useState('all')
    const [isCreating, setIsCreating] = useState(false)
    const [, setPreviewTest] = useState<Test | null>(null)
    const [editingTest, setEditingTest] = useState<Test | null>(null)

    // Menu action handlers
    const handlePreview = (test: Test) => {
        setPreviewTest(test)
    }

    const handleEdit = (test: Test) => {
        setEditingTest(test)
    }



    const handleDelete = (test: Test) => {
        if (confirm(`Are you sure you want to delete "${test.title}"?`)) {
            toast.success(`Test "${test.title}" deleted successfully!`)
            // In production, this would delete the test from the database
        }
    }



    const batches = getBatches()
    const categories: (TestCategory | 'all')[] = ['all', 'chapter', 'daily', 'weekly', 'monthly', 'full']
    const statuses: (TestManagementStatus | 'all')[] = ['all', 'draft', 'published', 'upcoming', 'active', 'completed']

    // Filter tests
    const filteredTests = mockTests.filter(test => {
        const matchesSearch = test.title.toLowerCase().includes(searchTerm.toLowerCase())
        const matchesCategory = selectedCategory === 'all' || test.category === selectedCategory
        const matchesStatus = selectedStatus === 'all' || test.status === selectedStatus
        const matchesBatch = selectedBatch === 'all' ||
            test.visibility.type === 'all' ||
            (test.visibility.targets?.includes(selectedBatch))
        return matchesSearch && matchesCategory && matchesStatus && matchesBatch
    })

    // Stats
    const stats = {
        total: mockTests.length,
        scheduled: mockTests.filter(t => t.status === 'upcoming' || t.status === 'published').length,
        active: mockTests.filter(t => t.status === 'active').length,
        avgScore: mockTestResults.length > 0
            ? Math.round(mockTestResults.reduce((sum, r) => sum + r.score, 0) / mockTestResults.length)
            : 0
    }

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
    }

    const formatTime = (dateStr: string) => {
        const date = new Date(dateStr)
        return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    }

    if (isCreating) {
        return <CreateTestView onCancel={() => setIsCreating(false)} />
    }

    if (editingTest) {
        return <CreateTestView onCancel={() => setEditingTest(null)} existingTest={editingTest} />
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h2 className="text-xl md:text-2xl font-bold text-theme-primary">Test Management</h2>
                    <p className="text-sm text-theme-secondary">Create and manage tests for your batches</p>
                </div>
                <button
                    onClick={() => setIsCreating(true)}
                    className="w-full sm:w-auto px-4 py-2.5 bg-primary-blue text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25"
                >
                    <Plus className="h-4 w-4" />
                    Create Test
                </button>
            </div>

            {/* Quick Stats - Responsive Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <GlassmorphicCard className="p-3 md:p-4 flex items-center gap-3">
                    <div className="p-2 md:p-3 rounded-xl bg-blue-100 dark:bg-blue-900/20 text-blue-600 shrink-0">
                        <FileText className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs text-theme-secondary truncate">Total Tests</p>
                        <p className="text-xl md:text-2xl font-bold text-theme-primary">{stats.total}</p>
                    </div>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-3 md:p-4 flex items-center gap-3">
                    <div className="p-2 md:p-3 rounded-xl bg-green-100 dark:bg-green-900/20 text-green-600 shrink-0">
                        <Calendar className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs text-theme-secondary truncate">Scheduled</p>
                        <p className="text-xl md:text-2xl font-bold text-theme-primary">{stats.scheduled}</p>
                    </div>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-3 md:p-4 flex items-center gap-3">
                    <div className="p-2 md:p-3 rounded-xl bg-amber-100 dark:bg-amber-900/20 text-amber-600 shrink-0">
                        <Play className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs text-theme-secondary truncate">Active</p>
                        <p className="text-xl md:text-2xl font-bold text-theme-primary">{stats.active}</p>
                    </div>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-3 md:p-4 flex items-center gap-3">
                    <div className="p-2 md:p-3 rounded-xl bg-purple-100 dark:bg-purple-900/20 text-purple-600 shrink-0">
                        <BarChart3 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs text-theme-secondary truncate">Avg Score</p>
                        <p className="text-xl md:text-2xl font-bold text-theme-primary">{stats.avgScore}</p>
                    </div>
                </GlassmorphicCard>
            </div>

            {/* Category Tabs - Horizontal Scroll on Mobile */}
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-2">
                <div className="flex gap-2 min-w-max">
                    {categories.map(cat => {
                        const isAll = cat === 'all'
                        const config = isAll ? null : categoryConfig[cat]
                        const Icon = config?.icon || Layers
                        const count = cat === 'all'
                            ? mockTests.length
                            : mockTests.filter(t => t.category === cat).length

                        return (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className={`flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${selectedCategory === cat
                                    ? 'bg-primary-blue text-white shadow-lg shadow-blue-500/25'
                                    : 'bg-white dark:bg-slate-800 text-theme-secondary hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                                    }`}
                            >
                                <Icon className="h-4 w-4" />
                                <span>{isAll ? 'All' : config?.label}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded-full ${selectedCategory === cat
                                    ? 'bg-white/20'
                                    : 'bg-slate-100 dark:bg-slate-700'
                                    }`}>
                                    {count}
                                </span>
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Filters Row - Stack on Mobile */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-secondary" />
                    <input
                        type="text"
                        placeholder="Search tests..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/20 outline-none transition-all"
                    />
                </div>
                <div className="flex gap-3">
                    <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value as TestManagementStatus | 'all')}
                        className="flex-1 sm:flex-none px-3 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm outline-none focus:border-primary-blue"
                    >
                        {statuses.map(s => (
                            <option key={s} value={s}>{s === 'all' ? 'All Status' : statusConfig[s].label}</option>
                        ))}
                    </select>
                    <select
                        value={selectedBatch}
                        onChange={(e) => setSelectedBatch(e.target.value)}
                        className="flex-1 sm:flex-none px-3 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm outline-none focus:border-primary-blue"
                    >
                        <option value="all">All Batches</option>
                        {batches.map(b => (
                            <option key={b} value={b}>{b}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tests List */}
            <div className="space-y-3">
                <AnimatePresence mode="popLayout">
                    {filteredTests.length === 0 ? (
                        <GlassmorphicCard className="p-12 text-center">
                            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                            <p className="text-theme-secondary">No tests found</p>
                            <p className="text-sm text-slate-400 mt-1">Try adjusting your filters or create a new test</p>
                        </GlassmorphicCard>
                    ) : (
                        filteredTests.map((test, idx) => (
                            <TestCard
                                key={test.id}
                                test={test}
                                index={idx}
                                formatDate={formatDate}
                                formatTime={formatTime}
                                onPreview={handlePreview}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                            />
                        ))
                    )}
                </AnimatePresence>
            </div>

            {/* Recent Results Section - Overall Exam Stats */}
            {mockTestResults.length > 0 && (
                <div>
                    <h3 className="text-lg font-bold text-theme-primary mb-3 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-primary-blue" />
                        Overall Results Summary
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <GlassmorphicCard className="p-4 text-center">
                            <p className="text-2xl font-bold text-theme-primary">{mockTestResults.length}</p>
                            <p className="text-xs text-theme-secondary">Total Attempts</p>
                        </GlassmorphicCard>
                        <GlassmorphicCard className="p-4 text-center">
                            <p className="text-2xl font-bold text-green-600">
                                {Math.round(mockTestResults.reduce((sum, r) => sum + r.score, 0) / mockTestResults.length)}
                            </p>
                            <p className="text-xs text-theme-secondary">Avg Score</p>
                        </GlassmorphicCard>
                        <GlassmorphicCard className="p-4 text-center">
                            <p className="text-2xl font-bold text-blue-600">
                                {Math.max(...mockTestResults.map(r => r.score))}
                            </p>
                            <p className="text-xs text-theme-secondary">Highest Score</p>
                        </GlassmorphicCard>
                        <GlassmorphicCard className="p-4 text-center">
                            <p className="text-2xl font-bold text-amber-600">
                                {Math.round((mockTestResults.filter(r => r.score >= 360).length / mockTestResults.length) * 100)}%
                            </p>
                            <p className="text-xs text-theme-secondary">Pass Rate</p>
                        </GlassmorphicCard>
                    </div>
                    <GlassmorphicCard className="p-4">
                        <h4 className="text-sm font-semibold text-theme-primary mb-3">Subject-wise Average</h4>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center">
                                <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full mb-2">
                                    <div className="h-full bg-purple-500 rounded-full" style={{ width: `${Math.round(mockTestResults.reduce((sum, r) => sum + r.subjectWise.physics, 0) / mockTestResults.length / 180 * 100)}%` }} />
                                </div>
                                <p className="text-lg font-bold text-purple-600">
                                    {Math.round(mockTestResults.reduce((sum, r) => sum + r.subjectWise.physics, 0) / mockTestResults.length)}
                                </p>
                                <p className="text-xs text-theme-secondary">Physics</p>
                            </div>
                            <div className="text-center">
                                <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full mb-2">
                                    <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${Math.round(mockTestResults.reduce((sum, r) => sum + r.subjectWise.chemistry, 0) / mockTestResults.length / 180 * 100)}%` }} />
                                </div>
                                <p className="text-lg font-bold text-cyan-600">
                                    {Math.round(mockTestResults.reduce((sum, r) => sum + r.subjectWise.chemistry, 0) / mockTestResults.length)}
                                </p>
                                <p className="text-xs text-theme-secondary">Chemistry</p>
                            </div>
                            <div className="text-center">
                                <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full mb-2">
                                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.round(mockTestResults.reduce((sum, r) => sum + r.subjectWise.biology, 0) / mockTestResults.length / 360 * 100)}%` }} />
                                </div>
                                <p className="text-lg font-bold text-emerald-600">
                                    {Math.round(mockTestResults.reduce((sum, r) => sum + r.subjectWise.biology, 0) / mockTestResults.length)}
                                </p>
                                <p className="text-xs text-theme-secondary">Biology</p>
                            </div>
                        </div>
                    </GlassmorphicCard>
                </div>
            )}
        </div>
    )
}

// Test Card Component
function TestCard({
    test,
    index,
    formatDate,
    formatTime,
    onPreview,
    onEdit,
    onDelete
}: {
    test: Test
    index: number
    formatDate: (d: string) => string
    formatTime: (d: string) => string
    onPreview: (test: Test) => void
    onEdit: (test: Test) => void
    onDelete: (test: Test) => void
}) {
    const catConfig = categoryConfig[test.category]
    const statConfig = statusConfig[test.status]
    const CatIcon = catConfig.icon

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ delay: index * 0.05 }}
        >
            <GlassmorphicCard className="p-4 hover:shadow-lg transition-all">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    {/* Left: Icon + Info */}
                    <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl bg-${catConfig.color}-100 dark:bg-${catConfig.color}-900/20 flex items-center justify-center shrink-0`}>
                            <CatIcon className={`h-5 w-5 md:h-6 md:w-6 text-${catConfig.color}-600`} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-theme-primary truncate">{test.title}</h3>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium ${statConfig.bg} ${statConfig.text}`}>
                                    {statConfig.label}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 md:gap-4 mt-1 text-xs text-theme-secondary flex-wrap">
                                <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {formatDate(test.schedule.startTime)}
                                </span>
                                <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatTime(test.schedule.startTime)}
                                </span>
                                <span className="flex items-center gap-1">
                                    <FileText className="h-3 w-3" />
                                    {test.questionCount}Q
                                </span>
                                <span className="hidden sm:flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    {test.visibility.type === 'all' ? 'All Users' : test.visibility.targets?.join(', ')}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Right: Stats + Actions */}
                    <div className="flex items-center gap-3 sm:gap-4 justify-between sm:justify-end">
                        {/* Subjects Pills - Hide on small mobile */}
                        <div className="hidden xs:flex gap-1">
                            {test.subjects.map(sub => (
                                <span
                                    key={sub}
                                    className={`px-2 py-0.5 rounded text-[10px] font-medium ${sub === 'Physics' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                        sub === 'Chemistry' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                            'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                        }`}
                                >
                                    {sub.charAt(0)}
                                </span>
                            ))}
                        </div>

                        {/* Duration & Marks */}
                        <div className="text-right">
                            <p className="text-sm font-bold text-theme-primary">{test.totalMarks}</p>
                            <p className="text-xs text-theme-secondary">{test.duration}min</p>
                        </div>

                        {/* Section Status for Collaborative Tests */}
                        {test.sections && (
                            <div className="hidden md:flex items-center gap-1">
                                {test.sections.map(sec => (
                                    <div
                                        key={sec.id}
                                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${sec.status === 'approved' ? 'bg-green-100 text-green-600' :
                                            sec.status === 'locked' ? 'bg-blue-100 text-blue-600' :
                                                sec.status === 'submitted' ? 'bg-amber-100 text-amber-600' :
                                                    'bg-slate-100 text-slate-600'
                                            }`}
                                        title={`${sec.subject}: ${sec.status}`}
                                    >
                                        {sec.subject.charAt(0)}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => onPreview(test)}
                                className="px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-medium flex items-center gap-1.5 transition-colors"
                                title="Preview Test"
                            >
                                <Eye className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Preview</span>
                            </button>
                            <button
                                onClick={() => onEdit(test)}
                                className="px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 text-xs font-medium flex items-center gap-1.5 transition-colors"
                                title="Edit Test"
                            >
                                <Edit className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Edit</span>
                            </button>
                            <button
                                onClick={() => onDelete(test)}
                                className="px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium flex items-center gap-1.5 transition-colors"
                                title="Delete Test"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Delete</span>
                            </button>
                        </div>
                    </div>
                </div>
            </GlassmorphicCard>
        </motion.div>
    )
}
